import { HttpErrorResponse } from '@angular/common/http';
import {
  QueryClient,
  QueryFunction,
  QueryFunctionContext,
  QueryKey,
  QueryObserver,
  QueryObserverOptions,
  QueryObserverResult
} from '@tanstack/query-core';
import { firstValueFrom, fromEvent, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type QueryOptions<
  TData,
  TQueryKey extends QueryKey = QueryKey,
  TError = HttpErrorResponse
> = QueryObserverOptions<TData, TError, TData, TData, TQueryKey>;

/**
 * A query result, plus:
 * - `queryKey`: the key of the query this result is for. While `isPlaceholderData` is true, that's the query
 *   being loaded, not the one the shown data came from.
 * - `isInvalidated`: whether the data is known to be out of date because it was invalidated, e.g. by a write.
 *   A refetch then is not a routine background refresh: the rows may be wrong.
 */
export type QueryResult<TData, TError = HttpErrorResponse, TQueryKey extends QueryKey = QueryKey> = QueryObserverResult<
  TData,
  TError
> & {
  queryKey: TQueryKey;
  isInvalidated: boolean;
};

/**
 * Turns a stream of query options into a stream of query results, backed by the shared cache.
 *
 * One `QueryObserver` lives for the whole subscription. Each new options value, such as
 * the next page, re-points it with `setOptions`. That is what lets `placeholderData: keepPreviousData`
 * keep showing the previous page's rows while the next one loads.
 *
 * Query errors arrive as `result.error`, not as an error on the returned Observable. It only errors if
 * `options$` does (e.g. the options factory throws), so a bug there fails loudly instead of loading forever.
 */
export function query$<TData, TQueryKey extends QueryKey = QueryKey, TError = HttpErrorResponse>(
  client: QueryClient,
  options$: Observable<QueryOptions<TData, TQueryKey, TError>>
): Observable<QueryResult<TData, TError, TQueryKey>> {
  return new Observable(subscriber => {
    let observer: QueryObserver<TData, TError, TData, TData, TQueryKey> | undefined;
    let unsubscribeObserver: (() => void) | undefined;

    const emit = (result: QueryObserverResult<TData, TError>) => {
      const query = observer.getCurrentQuery();
      subscriber.next({ ...result, queryKey: query.queryKey, isInvalidated: query.state.isInvalidated });
    };

    const optionsSubscription = options$.subscribe({
      next: options => {
        const defaultedOptions = client.defaultQueryOptions(options);

        if (observer) {
          // Notifies the subscriber below if the result changes.
          observer.setOptions(defaultedOptions);
          return;
        }

        observer = new QueryObserver(client, defaultedOptions);
        // Emit the current result straight away, so cached data renders without waiting a tick.
        emit(observer.getOptimisticResult(defaultedOptions));
        unsubscribeObserver = observer.subscribe(emit);
      },
      error: error => subscriber.error(error)
    });

    return () => {
      optionsSubscription.unsubscribe();
      unsubscribeObserver?.();
    };
  });
}

/**
 * Adapts an Observable-returning API call, like the HttpClient-based services, to a query function.
 *
 * If the query is cancelled (for example when the user pages on before it finishes), the HTTP
 * request is cancelled too, and none is made if it was cancelled before this ran.
 */
export function fromObservable<TData, TQueryKey extends QueryKey = QueryKey>(
  factory: (context: QueryFunctionContext<TQueryKey>) => Observable<TData>
): QueryFunction<TData, TQueryKey> {
  return context => {
    if (context.signal.aborted) return Promise.reject(new Error('Query was cancelled before it started'));
    return firstValueFrom(factory(context).pipe(takeUntil(fromEvent(context.signal, 'abort'))));
  };
}
